import ArticleLayout from './ArticleLayout';

export default function SafetyGuide() {
  return (
    <ArticleLayout
      title="Sugargoo Safety Guide: How to Shop Securely and Avoid Scams in 2026"
      subtitle="Stay safe while shopping on Sugargoo with our comprehensive safety guide. Learn how to identify trustworthy sellers, avoid common scams, protect your payment information, and ensure a secure shopping experience with every spreadsheet purchase."
      category="Safety Guide"
      breadcrumbs={[
        { name: 'Home', path: '/' },
        { name: 'Safety Guide', path: '/sugargoo-safety-guide' },
      ]}
      schemaJson={{
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'Sugargoo Safety Guide: How to Shop Securely and Avoid Scams in 2026',
        description: 'Stay safe shopping on Sugargoo with our guide covering trustworthy sellers, common scams, payment protection, and secure shopping tips.',
        author: { '@type': 'Organization', name: 'Sugargoo Spreadsheet Watch' },
        datePublished: '2026-06-14',
        dateModified: '2026-06-18',
        publisher: { '@type': 'Organization', name: 'Sugargoo Spreadsheet Watch' },
      }}
    >
      <section className="mb-12">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Why Safety Matters on Sugargoo</h2>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
          While the Sugargoo platform itself provides a secure intermediary between buyers and sellers, understanding the additional safety measures you can take will ensure every transaction goes smoothly. The Sugargoo spreadsheet links to thousands of products from hundreds of sellers, and while most are trustworthy, having a safety checklist in mind is essential for protecting your money and personal information.
        </p>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
          Sugargoo acts as a buyer agent, meaning you pay Sugargoo first, they purchase the item from the seller on your behalf, and only then does the seller ship to the Sugargoo warehouse. This adds a layer of protection, but it does not eliminate all risks — especially when dealing with sellers who have little to no feedback history.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Seller Trust Indicators</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200">
                <th className="text-left py-3 px-4 font-semibold text-foreground-900">Indicator</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground-900">Green Flag</th>
                <th className="text-left py-3 px-4 font-semibold text-foreground-900">Red Flag</th>
              </tr>
            </thead>
            <tbody>
              {[
                { indicator: 'Store Age', green: '1+ year old', red: 'Less than 3 months' },
                { indicator: 'Reviews', green: '100+ positive with photos', red: 'No reviews or all generic text' },
                { indicator: 'Product Photos', green: 'Real QC sample images', red: 'Only retail/stock photos' },
                { indicator: 'Response Time', green: 'Replies within 24 hours', red: 'Never responds to messages' },
                { indicator: 'Return Rate', green: 'Below 3% returns', red: 'Above 10% returns' },
              ].map((row) => (
                <tr key={row.indicator} className="border-b border-background-100 hover:bg-background-100/50 transition-colors">
                  <td className="py-3 px-4 font-medium text-foreground-900">{row.indicator}</td>
                  <td className="py-3 px-4 text-foreground-600">{row.green}</td>
                  <td className="py-3 px-4 text-foreground-600">{row.red}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Common Scams and How to Avoid Them</h2>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
          The most common scam on any agent platform is the bait-and-switch, where a seller advertises high-quality product photos but ships something of significantly lower quality. Always use the QC photo system to verify your actual item before approving shipment. If the QC photos do not match the advertised product, request a return immediately — Sugargoo provides a window for this.
        </p>
        <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
          Another frequent issue involves sellers who never ship the product after receiving payment. With Sugargoo, your money is held by the platform, not the seller, so you can request a refund if the seller fails to ship within a reasonable timeframe. This is one of the key advantages of using an agent platform rather than buying directly from unknown sellers.
        </p>
      </section>
    </ArticleLayout>
  );
}